import React, { useState } from 'react';
import { Mic, Loader2, ChevronDown } from 'lucide-react';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { persistProfilePref } from '../../utils/persistProfilePref';

/**
 * FloatingMicFAB — always-visible idle mic (Feb 26, 2026)
 *
 * Fixes a recurring complaint: provider has to scroll back up to find the
 * Dictate button after typing. Now there's a small mic FAB in the
 * bottom-right corner that's always reachable. Clicking it starts a
 * dictation; the active-recording UI is still handled by FloatingDictation
 * (drag-positioned, with pause/stop/level meter).
 *
 * Hidden states:
 *   • recording.isRecording → handled by FloatingDictation
 *   • recording.isTranscribing → handled by FloatingDictation
 *   • effectiveRole !== 'provider' → patients don't dictate
 *
 * The user can collapse the FAB to a tiny dot if they want it out of the way.
 */
const FloatingMicFAB = ({ recording, effectiveRole = 'provider' }) => {
  const { status: connectionStatus } = useConnectionStatus();
  const isOffline = connectionStatus === 'offline';
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('writeright_fab_mic_collapsed') === '1'; }
    catch { return false; }
  });

  // Hide when active recording UI is already on screen
  if (effectiveRole !== 'provider') return null;
  if (recording?.isRecording || recording?.isTranscribing) return null;

  const handleStart = () => {
    if (isOffline) return;
    recording?.startRecording?.();
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('writeright_fab_mic_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
    persistProfilePref({ fab_mic_collapsed: next });
  };

  if (collapsed) {
    // Tiny dot variant — middle-right; click expands
    return (
      <button
        type="button"
        onClick={toggleCollapse}
        className="fixed top-1/2 right-3 -translate-y-1/2 z-[60] w-8 h-8 rounded-full bg-blue-600/90 hover:bg-blue-500 border-2 border-blue-400/50 shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="floating-mic-fab-collapsed"
        title="Expand quick-dictate (click to reveal mic)"
        aria-label="Expand quick-dictate"
      >
        <Mic className="w-3.5 h-3.5 text-white" />
      </button>
    );
  }

  return (
    <div
      className="fixed top-1/2 right-3 -translate-y-1/2 z-[60] flex flex-col items-center gap-1.5"
      data-testid="floating-mic-fab"
    >
      <button
        type="button"
        onClick={handleStart}
        disabled={isOffline}
        className={`group flex items-center justify-center w-12 h-12 rounded-full shadow-xl border-2 backdrop-blur-md transition-all
          ${isOffline
            ? 'bg-slate-700/80 border-slate-500/50 text-slate-400 cursor-not-allowed'
            : 'bg-blue-600/95 border-blue-400/60 text-white hover:bg-blue-500 hover:scale-110 hover:shadow-2xl active:scale-95'}`}
        data-testid="floating-mic-fab-btn"
        aria-label={isOffline ? 'Dictate offline (disabled)' : 'Start dictation'}
        title={isOffline
          ? 'Dictation paused — needs internet'
          : 'Click to start dictation from anywhere on the page'}
      >
        {isOffline ? (
          <Loader2 className="w-5 h-5 opacity-50" />
        ) : (
          <Mic className="w-5 h-5 transition-transform group-hover:scale-110" />
        )}
      </button>
      {/* Collapse to tiny dot */}
      <button
        type="button"
        onClick={toggleCollapse}
        className="w-6 h-5 rounded-md bg-card/80 hover:bg-card border border-border text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
        data-testid="floating-mic-fab-collapse"
        title="Minimize quick-dictate"
        aria-label="Minimize quick-dictate"
      >
        <ChevronDown className="w-3 h-3" />
      </button>
    </div>
  );
};

export default FloatingMicFAB;
